import type {
  AriaAttributes,
  ChangeEvent,
  FocusEvent,
  LabelHTMLAttributes,
  ReactElement,
} from 'react'
import { useState } from 'react'

import type { SerializedStyles } from '@emotion/react'
import { isNil } from 'ramda'
import type { RegisterOptions, UseFormReturn } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

import type { RequireAtLeastOne } from '@twistopayments/design-system/utils'
import { hasString } from '@twistopayments/design-system/utils'

import { FormHelperText } from '../../atoms/form-helper-text'
import type { CardProviders, InputProps } from '../../atoms/input'
import {
  CardNumberInput,
  Input,
  StaticInput,
} from '../../atoms/input'
import type { InputLabelProps } from '../../atoms/input-label'
import { InputLabel } from '../../atoms/input-label'
import { PasswordInput } from '../password-input'

type BaseProps = {
  autoFocus?: boolean
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  error?: boolean
  helperText?: string | undefined | ReactElement
  label?: string | ReactElement
  ariaLabel?: string
  placeholder?: string
  type?: InputProps['type']
  nativeInputProps?: InputProps['nativeInputProps']
  inputLabelProps?: Partial<InputLabelProps> &
    LabelHTMLAttributes<HTMLLabelElement>
  inputProps?: Partial<InputProps>
  helperTextStyles?: SerializedStyles
  id?: string
  name: string
  value?: string | number
  rules?: RegisterOptions
  disabled?: boolean
  cardProvider?: CardProviders
} & Pick<InputProps, 'className' | 'styles'>

type StaticFieldProps = BaseProps & {
  onChange?: never
  variant: 'static'
}

export type DynamicFieldProps = RequireAtLeastOne<
  BaseProps,
  'label' | 'ariaLabel'
> & {
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  variant?: 'input' | 'password' | 'card'
}

type Props = DynamicFieldProps | StaticFieldProps

export const TextField = ({
  error: errorProp = false,
  helperText,
  label,
  ariaLabel,
  onBlur,
  onFocus,
  placeholder,
  value,
  type,
  nativeInputProps,
  inputLabelProps, // like `htmlFor`
  helperTextStyles,
  inputProps,
  id: nodeId,
  name,
  variant = 'input',
  rules,
  disabled,
  cardProvider = 'other',
  ...props
}: Props) => {
  const [isFocused, setFocused] = useState(false)
  const { formState, watch } = (useFormContext() as UseFormReturn | null) || {}
  const fieldErrors = formState?.errors ?? {}

  const id = nodeId ?? name
  const a11y: AriaAttributes = { 'aria-label': ariaLabel }

  const empty = watch ? !hasString(watch(name, value)) : !hasString(value)

  const readOnly = variant === 'static'
  const hasError = errorProp || Boolean(fieldErrors[name])

  const hasStartAdornment = !isNil(inputProps?.startAdornment)

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(false)
    if (onBlur) {
      onBlur(event)
    }
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(true)
    if (onFocus) {
      onFocus(event)
    }
  }

  const sharedProps = {
    placeholder,
    onBlur: handleBlur,
    onFocus: handleFocus,
    value,
    name,
    rules,
    disabled,
    'data-testid': 'text-field',
    ...props,
    nativeInputProps: {
      id,
      readOnly,
      ...nativeInputProps,
    },
    ...inputProps,
    ...a11y,
  }

  const renderField = () => {
    switch (variant) {
      case 'static':
        return <StaticInput border={false} {...sharedProps} />
      case 'password':
        return <PasswordInput {...sharedProps} error={errorProp} />
      case 'card':
        return (
          <CardNumberInput
            cardProvider={cardProvider}
            {...sharedProps}
            error={errorProp}
          />
        )
      default:
        return <Input type={type} {...sharedProps} error={errorProp} />
    }
  }

  return (
    <>
      {label !== undefined ? (
        <InputLabel
          disabled={disabled}
          flexible={empty && placeholder === undefined && !hasStartAdornment}
          focused={isFocused}
          htmlFor={id}
          {...inputLabelProps}
        >
          {label}
        </InputLabel>
      ) : null}
      {renderField()}
      <FormHelperText
        css={helperTextStyles}
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={name}
      >
        {helperText}
      </FormHelperText>
    </>
  )
}
